import axios from 'axios';
import type { Coach, PrivateLessonRequest } from '@court-sync/shared';
import { auth } from '../config/firebase';
import { API_BASE_URL } from '../config/api';

async function authApi() {
  let token = '';
  if (auth.currentUser) {
    try {
      token = await auth.currentUser.getIdToken();
    } catch {
      /* ignore */
    }
  }
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    },
  });
}

export interface PrivateBookingConfig {
  privateBookingEnabled: boolean;
  privateBookingMinDurationMinutes: number;
  privateBookingMaxDurationMinutes: number;
  privateBookingDurationStepMinutes: number;
  privateBookingApprovalRequired: boolean;
  /** Minimum hours from now before a session can start (default 24) */
  privateBookingMinLeadHours: number;
  currency: string;
}

export const privateBookingService = {
  async getConfig(academyId: string): Promise<PrivateBookingConfig> {
    const api = await authApi();
    const { data } = await api.get<{ success: boolean; data: PrivateBookingConfig }>(
      `/public/private-booking/${academyId}/config`
    );
    if (!data.success || !data.data) {
      throw new Error('Failed to load private booking config');
    }
    return data.data;
  },

  async listCoaches(academyId: string): Promise<Coach[]> {
    const api = await authApi();
    const { data } = await api.get<{ success: boolean; data: Coach[] }>(
      `/public/private-booking/${academyId}/coaches`
    );
    if (!data.success || !data.data) {
      throw new Error('Failed to load coaches');
    }
    return data.data;
  },

  async getCoachAvailability(academyId: string, coachId: string, dayOfWeek: number) {
    const api = await authApi();
    const { data } = await api.get<{
      success: boolean;
      data: { weeklySlots: unknown[]; exceptions: unknown[] };
    }>(`/public/private-booking/${academyId}/coach/${coachId}/availability`, {
      params: { dayOfWeek },
    });
    if (!data.success || !data.data) {
      throw new Error('Failed to load availability');
    }
    return data.data;
  },

  async getCoachAvailabilityAll(academyId: string, coachId: string, startFromDate?: string) {
    const api = await authApi();
    const { data } = await api.get<{
      success: boolean;
      data: {
        weeklySlots: unknown[];
        exceptions: unknown[];
        lessonOccupiedBlocks?: { dayOfWeek: number; startTime: string; endTime: string }[];
      };
    }>(`/public/private-booking/${academyId}/coach/${coachId}/availability-all`, {
      params: startFromDate ? { startFromDate } : undefined,
    });
    if (!data.success || !data.data) {
      throw new Error('Failed to load availability');
    }
    return data.data;
  },

  async preview(
    body:
      | {
          academyId: string;
          studentId: string;
          coachId: string;
          sessionSlots: Array<{ sessionDate: string; startTime: string; endTime: string }>;
        }
      | {
          academyId: string;
          studentId: string;
          coachId: string;
          dayOfWeek: number;
          startTime: string;
          endTime: string;
          numberOfSessions: number;
          startFromDate: string;
        }
  ) {
    const api = await authApi();
    const { data } = await api.post<{
      success: boolean;
      data: {
        totalAmount: number;
        currency: string;
        generatedDates: string[];
        durationMinutes: number;
        sessionSlots?: Array<{ sessionDate: string; startTime: string; endTime: string }>;
      };
    }>('/public/private-booking/preview', body);
    if (!data.success || !data.data) {
      throw new Error('Preview failed');
    }
    return data.data;
  },

  async createRequest(
    body:
      | {
          academyId: string;
          studentId: string;
          coachId: string;
          sessionSlots: Array<{ sessionDate: string; startTime: string; endTime: string }>;
        }
      | {
          academyId: string;
          studentId: string;
          coachId: string;
          dayOfWeek: number;
          startTime: string;
          endTime: string;
          numberOfSessions: number;
          startFromDate: string;
        }
  ) {
    const api = await authApi();
    const { data } = await api.post<{
      success: boolean;
      data: { requestId: string; lessonId?: string; bookingId?: string };
    }>('/public/private-booking/request', body);
    if (!data.success || !data.data) {
      throw new Error('Request failed');
    }
    return data.data;
  },

  async myRequests(): Promise<PrivateLessonRequest[]> {
    const api = await authApi();
    const { data } = await api.get<{ success: boolean; data: PrivateLessonRequest[] }>(
      '/private-booking/my-requests'
    );
    if (!data.success || !data.data) {
      throw new Error('Failed to load requests');
    }
    return data.data;
  },

  async listAcademyRequests(academyId: string): Promise<PrivateLessonRequest[]> {
    const api = await authApi();
    const { data } = await api.get<{ success: boolean; data: PrivateLessonRequest[] }>(
      '/private-booking/requests',
      { params: { academyId } }
    );
    if (!data.success || !data.data) {
      throw new Error('Failed to load requests');
    }
    return data.data;
  },

  async approveRequest(requestId: string, courtId: string) {
    const api = await authApi();
    const { data } = await api.put<{ success: boolean; data: { lessonId: string; bookingId: string } }>(
      `/private-booking/requests/${requestId}/approve`,
      { courtId }
    );
    if (!data.success || !data.data) {
      throw new Error('Approve failed');
    }
    return data.data;
  },

  async rejectRequest(requestId: string, reason: string) {
    const api = await authApi();
    await api.put(`/private-booking/requests/${requestId}/reject`, { reason });
  },

  /** Owner/admin: create a private lesson booking for a guardian + student (skips approval; creates lesson + booking). */
  async createManualRequest(body: {
    academyId: string;
    customerId: string;
    studentId: string;
    coachId: string;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    numberOfSessions: number;
    startFromDate: string;
  }) {
    const api = await authApi();
    const { data } = await api.post<{
      success: boolean;
      data: { requestId: string; lessonId?: string; bookingId?: string };
    }>('/private-booking/manual-request', body);
    if (!data.success || !data.data) {
      throw new Error('Could not create booking');
    }
    return data.data;
  },
};
